import { omit } from "lodash"
import React, { useState } from "react"
import { Col, Form, InputGroup } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import ChargeableWeight, { volumeWeight } from "../../components/Tables/ChargeableWeight"
import { maxWeightLimit, UNITS } from "../../constants/constants"

const ConsolAccordionBody = ({
    dimValue,
    dimFieldName,
    unitFieldName,
    unitValue,
    dimsAndWeightUnit,
    setDimsAndWeightUnit,
    boxWeightAndDims,
    setBoxWeightAndDims,
    units,
    setWeightLimitExceeded,
    setSelectedBoxes,
    boxes,
    errors,
    setErrors,
    warehouse,
    pallet = false,
    anyStep
}: {
    dimValue: any
    dimFieldName: string
    unitFieldName: string
    unitValue: any
    dimsAndWeightUnit: Record<string, any>
    setDimsAndWeightUnit: Function
    boxWeightAndDims: Record<string, any>
    setBoxWeightAndDims: Function
    units: string[]
    setWeightLimitExceeded: Function
    setSelectedBoxes: Function
    boxes: string[]
    errors: Record<string, any>
    setErrors: Function
    warehouse: number
    pallet?: boolean
    anyStep?: boolean
}) => {
    const { t, i18n } = useTranslation()
    const [weightAndDims, setWeightAndDims] = useState(boxWeightAndDims ?? ({} as Record<string, any>))
    const handleUnitChange = (e: any) => {
        if (unitFieldName === "weightUnit") {
            setDimsAndWeightUnit({
                ...dimsAndWeightUnit,
                [e.target.name]: [e.target.value],
                dimsUnit: UNITS[`${warehouse}`]?.DIM
            })
        } else {
            setDimsAndWeightUnit({
                ...dimsAndWeightUnit,
                [e.target.name]: [e.target.value],
                weightUnit: UNITS[`${warehouse}`]?.WEIGHT
            })
        }
    }

    const calculateChargeableWeight = (): number => {
        if (!dimsAndWeightUnit?.weightUnit) {
            setDimsAndWeightUnit({ ...dimsAndWeightUnit, weightUnit: UNITS[`${warehouse}`]?.WEIGHT })
        }
        const calculatedWeight = volumeWeight(
            boxWeightAndDims?.height,
            boxWeightAndDims?.length,
            boxWeightAndDims?.width,
            dimsAndWeightUnit?.weightUnit
        )

        const returnedValue = pallet
            ? boxWeightAndDims?.weight
            : ChargeableWeight(parseFloat(boxWeightAndDims?.weight), calculatedWeight)

        return returnedValue
    }

    return (
        <>
            {/* <Col
                className="px-1 d-flex justify-content-start align-items-center font-12"
                lg={pallet ? 2 : 4}
            > */}
            <Form.Group className={`mb-3 w-100`} as={Col}>
                <Form.Label htmlFor="boxHeight" className={dimFieldName === "ch_weight" ? "" : "required"}>
                    {t(!pallet ? `Box ${dimFieldName}` : dimFieldName)}
                </Form.Label>
                <InputGroup className="custom-inputgroup">
                    <InputGroup.Text>
                        <Form.Select
                            aria-label="units"
                            placeholder="--"
                            name={unitFieldName}
                            value={unitValue}
                            className="font-12"
                            onChange={(e: any) => handleUnitChange(e)}>
                            {units?.map((unit: string) => (
                                <option key={`unit-${unit}`} value={unit}>
                                    {unit}
                                </option>
                            ))}
                        </Form.Select>
                    </InputGroup.Text>
                    <Form.Control
                        min={0}
                        type="number"
                        autoComplete="off"
                        className="rounded-right font-12"
                        id={dimFieldName}
                        value={weightAndDims[dimFieldName]}
                        name={dimFieldName}
                        onChange={(e: any) => {
                            if (dimFieldName !== "ch_weight") {
                                if (!e.target.value || e.target.value === "") {
                                    setErrors({
                                        ...errors,
                                        dimFieldName: `This field is required, and must be more than 0`
                                    })
                                } else {
                                    setErrors({ ...omit(errors, [`${dimFieldName}`]) })
                                }
                            }
                            setWeightAndDims({
                                ...weightAndDims,
                                [e.target.name]: parseFloat(e.target.value)
                            })
                            setBoxWeightAndDims({
                                ...boxWeightAndDims,
                                [e.target.name]: parseFloat(e.target.value)
                            })
                            if (!pallet) {
                                setWeightLimitExceeded(0)
                                setSelectedBoxes([])
                                boxes?.forEach((box: string) => {
                                    const checkBox = document.getElementById(
                                        `checkbox-${box}`
                                    ) as HTMLInputElement
                                    checkBox.checked = false
                                })
                                if (
                                    dimFieldName === "weight" &&
                                    (parseFloat(e.target.value) > maxWeightLimit ||
                                        parseFloat(calculateChargeableWeight()?.toFixed(2)) > maxWeightLimit)
                                ) {
                                    setErrors({
                                        ...errors,
                                        weight: `Chargeable weight cannot exceed ${maxWeightLimit} lb`
                                    })
                                }
                                // else if ( dimFieldName === "weight" &&
                                //     (parseFloat(e.target.value) > parseFloat(calculateChargeableWeight()?.toFixed(2)))
                                // ) {
                                //     setErrors({ ...errors, "weight": "Actual weight cannot exceed volumetric weight" })
                                // }
                                else {
                                    setErrors({})
                                }
                            }
                        }}
                        // placeholder={`Box ${dimFieldName} ...`}
                        isInvalid={!!errors[dimFieldName]}
                        {...(anyStep ? { step: "any" } : {})}
                    />
                </InputGroup>
                {errors[dimFieldName] && errors[dimFieldName]?.length > 0 && (
                    <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                        {t(`${errors[dimFieldName]}`)}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            {/* </Col> */}
            {dimFieldName === "weight" && !pallet && (
                <Form.Group className={`mb-3 w-100`} as={Col}>
                    <Form.Label htmlFor="calculatedChWeight">{t("Calculated CH. Weight")}</Form.Label>
                    <InputGroup className="custom-inputgroup">
                        <InputGroup.Text>
                            <Form.Select
                                aria-label="units"
                                placeholder="--"
                                name={unitFieldName}
                                value={unitValue}
                                disabled
                                className="font-12"
                                onChange={(e: any) => handleUnitChange(e)}>
                                {units?.map((unit: string) => (
                                    <option key={`unit-${unit}`} value={unit}>
                                        {unit}
                                    </option>
                                ))}
                            </Form.Select>
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            name="calculatedChWeight"
                            className=""
                            value={
                                calculateChargeableWeight()?.toFixed(2) !== "NaN"
                                    ? calculateChargeableWeight()?.toFixed(2)
                                    : 0
                            }
                            placeholder="total chargeable weight..."
                            readOnly
                        />
                    </InputGroup>
                </Form.Group>
            )}
        </>
    )
}

export default ConsolAccordionBody
